import { useCallback, useMemo } from 'react'

import { accountOf, maskedNumbersText, NO_ACCOUNT_LABEL } from './accounts'
import { isUnfiledPile, type ProjectSplit } from './fromProject'
import { pagesNumbered } from './lookup'
import { formatMonth, formatPeriod } from './period'
import type { BandSource } from './strip'
import { statementsInAccountOrder } from './stripOrder'
import type { Account, Statement, StatementView } from './types'

// A band has to read on its own, so it carries the number as well as the dates. A band one
// page wide has room for the month only, and a cut-off date is worse than a short one.
const bandChipLabel = (
  accounts: Account[],
  statement: Statement | undefined,
  pageCount: number,
) => {
  if (!statement) return 'No statement'
  if (isUnfiledPile(statement.id)) return 'Left out by the split'
  const account = accountOf(accounts, statement.accountId)
  const number = account ? maskedNumbersText(account) : NO_ACCOUNT_LABEL
  const { period } = statement
  const dates =
    pageCount === 1 && period ? formatMonth(period.start) : formatPeriod(period)
  return `${number} · ${dates}`
}

// Both stay the same between renders until the split changes: the strip re-measures on a new one.
export const useStripBands = (split: ProjectSplit, views: StatementView[]) => {
  const { arrangement, document: sourceDocument } = split
  // What the strip draws: the statements in its own order, each holding its pages themselves.
  // A page number the document no longer has is dropped here rather than drawn as a hole.
  const stripStatements = useMemo(
    () =>
      statementsInAccountOrder(views, arrangement.accounts).map((held) => ({
        statement: held.statement,
        pages: pagesNumbered(sourceDocument, held.pages),
      })),
    [views, arrangement.accounts, sourceDocument],
  )
  const labelForBand = useCallback(
    (source: BandSource) =>
      bandChipLabel(
        arrangement.accounts,
        source.statement,
        source.pages.length,
      ),
    [arrangement.accounts],
  )
  return { stripStatements, labelForBand }
}
